import React, {useCallback, useRef, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'

const ConfirmContext = React.createContext(null)

/**
 * Coloque <ConfirmDialogProvider> na raiz da aplicação (ex: App.js),
 * e use o hook useConfirm() em qualquer componente filho.
 *
 * Exemplo em App.js:
 *   <ConfirmDialogProvider>
 *     <App />
 *   </ConfirmDialogProvider>
 *
 * Exemplo de uso:
 *   const confirm = useConfirm()
 *   const ok = await confirm('Deseja excluir?', 'Atenção')
 *   if (ok) { ... }
 */
export function ConfirmDialogProvider({children}) {
  const [state, setState] = useState({
    open: false,
    message: '',
    title: 'Confirmar',
    btnCancel: 'Cancelar',
    btnConfirm: 'Confirmar',
  })
  const resolveRef = useRef(null)

  const confirm = useCallback((message, p) => {
    setState({
      ...state,
      ...p,
      open: true,
      message
    })
    return new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  function handleConfirm() {
    setState(s => ({...s, open: false}))
    resolveRef.current?.(true)
  }

  function handleCancel() {
    setState(s => ({...s, open: false}))
    resolveRef.current?.(false)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal show={state.open} onHide={handleCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{fontSize: '16px'}}>{state.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{state.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={handleCancel}>{state.btnCancel}</Button>
          <Button variant="primary" size="sm" onClick={handleConfirm}>{state.btnConfirm}</Button>
        </Modal.Footer>
      </Modal>
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  return React.useContext(ConfirmContext)
}

/**
 * HOC para os class components, que não podem usar o hook useConfirm().
 * Injeta a prop `confirm`, com a mesma assinatura: confirm(message, options) => Promise<boolean>
 *
 *   class MinhaPage extends React.Component {
 *     async excluir(id) {
 *       if (!await this.props.confirm('Deseja realmente excluir?', {title: 'Alerta'})) return
 *       ...
 *     }
 *   }
 *   export default withConfirm(connect(...)(MinhaPage))
 */
export function withConfirm(Component) {
  function WithConfirm(props) {
    const confirm = useConfirm()
    return <Component {...props} confirm={confirm}/>
  }

  WithConfirm.displayName = `withConfirm(${Component.displayName || Component.name || 'Component'})`
  return WithConfirm
}
